/* =============================================================================
   CORE / DELIVERY-ROUTING — Default-Provider-Resolution aus Settings + Order
   =============================================================================
   Session 59 / Phase 48 (Block C)
   ----------------------------------------------------------------------------
   Memory `feedback_gg_counter_single_source`: zentrale Logik, keine
   Duplikation. Wer entscheiden muss „welcher Fahrer-Provider liefert
   diese Order?" → ruft `resolveDeliveryProvider({order, settings})`.

   Default-Verhalten aus Settings → Toggles definieren die ZULÄSSIGEN
   Provider. Operator hat pro Order Override-Möglichkeit (S60-UI), aber
   95 % der Orders laufen über die Auto-Resolution hier.

   Drei Provider:
     internal       → eigener Fahrer (G&G-Personal)
     uber_direct    → Uber-Driver via Uber Direct API (für eigene Web-Orders)
     uber_logistics → Uber-Driver für Uber-Marketplace-Order (Standard-Modus)

   Settings-Schema (gepflegt in restaurants.settings.delivery, phase_48):
     {
       internal_drivers_enabled: bool,       // eigener Fahrer aktiv
       uber_marketplace_enabled: bool,       // auf Uber Eats gelistet
       uber_marketplace_fulfillment: 'self' | 'uber',  // wer liefert bei MP-Order
       uber_direct_fallback_enabled: bool,   // Uber Direct als Spike-Catcher
       default_prep_minutes: number
     }
============================================================================ */

import {
  SOURCE_CHANNEL_MARKETPLACE,
  SOURCE_PARTNER_UBER_EATS
} from './order-sources.js';
import { safeInvoke } from './edge-functions.js';

// DB-CHECK-Werte für orders.delivery_provider:
export const DELIVERY_PROVIDER_INTERNAL       = 'internal';
export const DELIVERY_PROVIDER_UBER_DIRECT    = 'uber_direct';
export const DELIVERY_PROVIDER_UBER_LOGISTICS = 'uber_logistics';

export const DELIVERY_PROVIDERS = Object.freeze([
  DELIVERY_PROVIDER_INTERNAL,
  DELIVERY_PROVIDER_UBER_DIRECT,
  DELIVERY_PROVIDER_UBER_LOGISTICS
]);

export const DELIVERY_PROVIDER_LABELS = Object.freeze({
  [DELIVERY_PROVIDER_INTERNAL]:       'Eigener Fahrer',
  [DELIVERY_PROVIDER_UBER_DIRECT]:    'Uber Direct',
  [DELIVERY_PROVIDER_UBER_LOGISTICS]: 'Uber-Fahrer'
});

export function deliveryProviderLabel(value) {
  return DELIVERY_PROVIDER_LABELS[value] || '—';
}

/**
 * Liest Delivery-Settings defensiv (Tenant kann settings.delivery noch
 * unkonfiguriert haben → Defaults aus Migration werden vom UI rendern).
 */
function _settingsOf(input) {
  if (!input) return {};
  return input.delivery || input.deliverySettings || input;
}

/**
 * Berechnet den Default-Delivery-Provider für eine NEUE Delivery-Order,
 * basierend auf source_channel + source_partner + Settings-Toggles.
 *
 * Liefert: {
 *   provider:    'internal' | 'uber_direct' | 'uber_logistics' | null,
 *   reason:      menschen-lesbarer String, wieso dieser Provider gewählt wurde,
 *   alternative: optional zweite Wahl falls Operator override möchte,
 *   blocked:     true wenn überhaupt kein Provider zulässig ist
 * }
 *
 * Aufruf-Beispiele:
 *   resolveDeliveryProvider({order: {sourceChannel:'web', serviceType:'delivery'}, settings})
 *   → {provider:'internal', reason:'Eigene Fahrer aktiv'}
 *
 *   resolveDeliveryProvider({order: {sourceChannel:'marketplace', sourcePartner:'uber_eats', serviceType:'delivery'}, settings})
 *   → {provider:'uber_logistics', reason:'Marketplace-Order, Uber-Fulfillment konfiguriert'}
 *      ODER {provider:'internal', reason:'Marketplace-Order, Self-Delivery konfiguriert'}
 */
export function resolveDeliveryProvider({ order, settings } = {}) {
  if (!order || (order.serviceType || order.service_type) !== 'delivery') {
    return { provider: null, reason: 'Order ist keine Delivery-Order', blocked: false };
  }

  const s = _settingsOf(settings);
  const sourceChannel = order.sourceChannel || order.source_channel || '';
  const sourcePartner = order.sourcePartner || order.source_partner || '';

  const internalEnabled       = s.internal_drivers_enabled !== false; // default ON
  const uberMarketplaceMode   = (s.uber_marketplace_fulfillment === 'uber') ? 'uber' : 'self';
  const uberDirectFallback    = s.uber_direct_fallback_enabled === true;

  // --- Marketplace-Order (von Uber etc. via Webhook reingekommen) -----------
  if (sourceChannel === SOURCE_CHANNEL_MARKETPLACE) {
    if (sourcePartner === SOURCE_PARTNER_UBER_EATS) {
      if (uberMarketplaceMode === 'self') {
        if (internalEnabled) {
          return {
            provider: DELIVERY_PROVIDER_INTERNAL,
            reason: 'Uber-Eats-Order, Self-Delivery konfiguriert',
            alternative: DELIVERY_PROVIDER_UBER_LOGISTICS,
            blocked: false
          };
        }
        // Self konfiguriert aber kein eigener Fahrer aktiv → fallback auf Uber-Logistik
        return {
          provider: DELIVERY_PROVIDER_UBER_LOGISTICS,
          reason: 'Uber-Eats-Order, Self-Delivery konfiguriert aber keine eigenen Fahrer aktiv',
          blocked: false
        };
      }
      return {
        provider: DELIVERY_PROVIDER_UBER_LOGISTICS,
        reason: 'Uber-Eats-Order, Uber-Logistik konfiguriert',
        alternative: internalEnabled ? DELIVERY_PROVIDER_INTERNAL : null,
        blocked: false
      };
    }
    // Andere Marketplace-Partner (Lieferando, später) — Logistik default beim Partner
    return {
      provider: DELIVERY_PROVIDER_UBER_LOGISTICS, // Konvention: alle MP-Logistik
      reason: `Marketplace-Order von ${sourcePartner}, Standard-Logistik beim Partner`,
      alternative: internalEnabled ? DELIVERY_PROVIDER_INTERNAL : null,
      blocked: false
    };
  }

  // --- Eigene Order (cockpit oder web) -------------------------------------
  if (internalEnabled) {
    return {
      provider: DELIVERY_PROVIDER_INTERNAL,
      reason: 'Eigener Fahrer aktiv',
      alternative: uberDirectFallback ? DELIVERY_PROVIDER_UBER_DIRECT : null,
      blocked: false
    };
  }
  if (uberDirectFallback) {
    return {
      provider: DELIVERY_PROVIDER_UBER_DIRECT,
      reason: 'Keine eigenen Fahrer aktiv, Uber Direct Fallback konfiguriert',
      blocked: false
    };
  }
  // Weder eigene Fahrer noch Uber Direct → Order kann nicht ausgeliefert werden.
  return {
    provider: null,
    reason: 'Kein Lieferservice aktiv — Order kann nicht ausgeliefert werden. Bitte in Settings → Lieferung konfigurieren.',
    blocked: true
  };
}

/**
 * Validiert eine delivery_address (JSONB-Snapshot). Liefert
 * { ok: true, normalized: {...} } bei Gültigkeit oder
 * { ok: false, error: 'Grund' } bei Fehler.
 *
 * Mindest-Felder: street, plz, city. floor + notes optional.
 * Diese Validation ist der SINGLE-SOURCE für sowohl Customer-Form als auch
 * Cockpit-Eingabe und Edge-Function-Webhook-Mapping.
 */
export function validateDeliveryAddress(addr) {
  if (!addr || typeof addr !== 'object') {
    return { ok: false, error: 'Adresse fehlt.' };
  }
  const street = (addr.street || '').trim();
  const plz    = (addr.plz || addr.postal_code || addr.postalCode || '').trim();
  const city   = (addr.city || '').trim();

  if (!street) return { ok: false, error: 'Straße + Hausnummer fehlen.' };
  if (!plz)    return { ok: false, error: 'Postleitzahl fehlt.' };
  if (!/^\d{4,5}$/.test(plz)) return { ok: false, error: 'PLZ muss 4–5 Ziffern haben.' };
  if (!city)   return { ok: false, error: 'Stadt fehlt.' };

  return {
    ok: true,
    normalized: {
      street,
      plz,
      city,
      floor: (addr.floor || '').trim() || null,
      notes: (addr.notes || '').trim() || null,
      country: (addr.country || 'DE').trim().toUpperCase()
    }
  };
}

// =============================================================================
// UBER-API-STUBS (S60 Block A2 — Foundation für S61)
// =============================================================================
// Jonas-Direktive S60: „Vorbereiten, nicht integrieren".
// Diese Funktionen sind die zentralen Hooks, an denen die Uber-API-Anbindung
// in S61 eingehakt wird. Aktuell: leere No-Ops mit klarer Doku, was rein muss.
//
// Wer Uber später integriert, muss NUR diese 4 Funktionen befüllen und die
// Supabase-Secrets setzen — der Rest des Codes (resolveDeliveryProvider,
// Order-Erfassung, Settings-Mode) ist bereits vorbereitet.
// =============================================================================

/**
 * Hook 1: wird nach erfolgreichem create_customer_order ODER create_admin_order
 * mit service_type='delivery' aufgerufen, WENN der resolveDeliveryProvider-
 * Output 'uber_direct' oder 'uber_logistics' ergibt.
 *
 * @deprecated Seit S66 (2026-05-27) NICHT-AKTIV IM PILOT-SCOPE.
 *   Jonas-Direktive (S65): Uber Direct fällt für den Pilot weg, Eats Marketplace
 *   wird über manager.ubereats.com gehen (siehe docs/uber-onboarding-checklist.md).
 *   Funktion bleibt als Foundation erhalten — early-return mit Log, kein API-Call.
 *   Re-Aktivierung: Block aus dem if entfernen, sobald Direct-Sandbox wieder im Scope.
 *
 * S61-Implementation:
 *   1. Settings.delivery.uber_connection.oauth_token_ref aus DB lesen
 *   2. Access-Token refreshen via Edge-Function `uber-auth`
 *   3. Edge-Function `uber-direct-create-delivery` aufrufen mit:
 *      - pickup_address: Restaurant-Adresse (aus settings.restaurant)
 *      - dropoff_address: order.delivery_address
 *      - items: order_items + total_gross_cents
 *      - external_id: order.id
 *   4. Response: uber_delivery_id + tracking_url → in orders zurückschreiben
 *
 * @param {{order: object, settings: object}} ctx
 * @returns {Promise<{ok: boolean, uberDeliveryId?: string, error?: string}>}
 */
export async function notifyUberOrderCreated(ctx) {
  // S66: Uber-Direct nicht im Pilot-Scope — Hook ist No-Op + Log.
  // Begründung in docs/uber-onboarding-checklist.md §0 + STATUS.md S65.
  console.info('[delivery-routing] notifyUberOrderCreated() skipped — Uber Direct NICHT-AKTIV im Pilot (S66).');
  return { ok: false, error: 'uber-direct-deactivated-s66-pilot-scope' };
  /* eslint-disable no-unreachable */
  // S63 — Foundation: ruft Edge-Function `uber-direct-create-delivery`.
  // Defensive: ohne supabase-Client (z.B. im Test) → no-op return.
  // Ohne Uber-Credentials → Edge-Function liefert 503, wir geben das 1:1 weiter.
  const order    = ctx && ctx.order;
  const settings = ctx && ctx.settings;
  if (!order || (order.serviceType || order.service_type) !== 'delivery') {
    return { ok: false, error: 'not-a-delivery-order' };
  }
  // Address aus Restaurant-Settings ableiten (pickup)
  const rest = (settings && settings.restaurant) || {};
  const pickup = {
    name:        rest.name || 'Restaurant',
    street:      rest.street || rest.address || '',
    city:        rest.city || '',
    postal_code: rest.postal_code || rest.plz || '',
    country:     rest.country || 'DE',
    phone:       rest.phone || ''
  };
  const dropoff = order.deliveryAddress || order.delivery_address || {};
  const items = (order.items || order.orderItems || []).map(it => ({
    name:        it.name || it.menuItemName || '',
    quantity:    Number(it.qty || it.quantity || 1),
    price_cents: Number(it.unitPriceCents || it.unit_price_cents || 0)
  }));
  const customer = {
    name:  order.customerName || order.guestName || '',
    phone: order.customerPhone || order.guestPhone || ''
  };
  try {
    const { data, error } = await safeInvoke('uber-direct-create-delivery', {
      order_id:          order.id,
      pickup,
      dropoff,
      items,
      total_gross_cents: Number(order.totalGrossCents || order.total_gross_cents || 0),
      currency:          'EUR',
      customer
    });
    if (error) return { ok: false, error: error.message || String(error) };
    if (!data || !data.ok) return { ok: false, error: data && data.error || 'unknown' };
    return {
      ok: true,
      uberDeliveryId: data.uber_delivery_id,
      trackingUrl:    data.tracking_url,
      feeCents:       data.fee_cents || null
    };
  } catch (e) {
    return { ok: false, error: String(e && e.message || e) };
  }
  /* eslint-enable no-unreachable */
}

/**
 * Hook 2: wird aufgerufen wenn sich der Status einer Uber-zu-liefernden Order
 * ändert (z.B. wenn Küche „ready" markiert). Pusht den neuen Status an Uber,
 * damit der Fahrer zur Abholung gerufen wird.
 *
 * @deprecated Seit S66 NICHT-AKTIV IM PILOT-SCOPE — siehe notifyUberOrderCreated().
 *
 * S61-Implementation:
 *   - Edge-Function `uber-update-delivery-status` mit uber_delivery_id + status
 *
 * @param {{order: object, newStatus: string}} ctx
 */
export async function pushOrderStatusToUber(ctx) {
  // S66: Uber-Direct nicht im Pilot-Scope — Hook ist No-Op + Log.
  console.info('[delivery-routing] pushOrderStatusToUber() skipped — Uber Direct NICHT-AKTIV im Pilot (S66).');
  return { ok: false, error: 'uber-direct-deactivated-s66-pilot-scope' };
  /* eslint-disable no-unreachable */
  // S63 — Foundation: ruft Edge-Function `uber-update-delivery-status`.
  const order = ctx && ctx.order;
  const newStatus = ctx && ctx.newStatus;
  if (!order || !newStatus) return { ok: false, error: 'order-or-status-missing' };
  const provider = order.deliveryProvider || order.delivery_provider;
  if (provider !== DELIVERY_PROVIDER_UBER_DIRECT && provider !== DELIVERY_PROVIDER_UBER_LOGISTICS) {
    return { ok: false, error: 'not-an-uber-order' };
  }
  try {
    const { data, error } = await safeInvoke('uber-update-delivery-status', {
      order_id:         order.id,
      new_status:       newStatus,
      delivery_provider: provider,
      uber_delivery_id: order.uberDeliveryId || order.uber_delivery_id || null,
      uber_order_id:    order.externalId || order.external_id || null
    });
    if (error) return { ok: false, error: error.message || String(error) };
    if (!data || !data.ok) return { ok: false, error: data && data.error || 'unknown' };
    return { ok: true, state: data.state, provider: data.provider };
  } catch (e) {
    return { ok: false, error: String(e && e.message || e) };
  }
  /* eslint-enable no-unreachable */
}

/**
 * Hook 3: Webhook-Handler-Entry, der von der Edge-Function `uber-eats-order-webhook`
 * pro eingehender Uber-Eats-Bestellung gerufen wird. Mappt das Uber-Schema
 * auf unser orders + order_items-Schema und legt die Order mit
 * source_channel='marketplace', source_partner='uber_eats' an.
 *
 * S61-Implementation:
 *   - HMAC-Signatur-Validation gegen webhook_secret_id
 *   - Order-Mapping (Uber-Item-IDs ↔ unsere menu_item.id)
 *   - INSERT in orders + order_items via create_admin_order-RPC
 *   - Realtime-Event → Cockpit zeigt eingehende Order
 *
 * @param {{uberOrderPayload: object}} ctx
 */
export async function handleIncomingUberEatsOrder(_ctx) {
  // S63 — Hinweis: Mapping + create_admin_order läuft in der Edge-Function
  // `uber-eats-order-webhook` direkt mit service_role, weil Uber niemals
  // einen authentifizierten User-Token mitschickt. Dieser Hook bleibt als
  // Frontend-Stub für Test-Tools (z.B. Replay alter Payloads).
  return { ok: false, error: 'inbound-route-runs-in-edge-function-uber-eats-order-webhook' };
}

/**
 * Hook 4: prüft ob für den aktuellen Tenant die Uber-Verbindung konfiguriert ist.
 * UI nutzt das für „Uber-Verbindung: nicht konfiguriert"-Hinweise im Settings-Tab
 * und um Uber-basierte Routing-Entscheidungen zu blockieren wenn keine API steht.
 *
 * @param {object} settings - state.settings (mit settings.delivery.uber_connection)
 * @returns {boolean}
 */
export function isUberConnectionConfigured(settings) {
  const conn = settings && settings.delivery && settings.delivery.uber_connection;
  return !!(conn && conn.configured && conn.account_id && conn.oauth_token_ref);
}

// ---------------------------------------------------------------------------
// window-Export
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.DeliveryRouting = {
    DELIVERY_PROVIDER_INTERNAL,
    DELIVERY_PROVIDER_UBER_DIRECT,
    DELIVERY_PROVIDER_UBER_LOGISTICS,
    DELIVERY_PROVIDERS,
    DELIVERY_PROVIDER_LABELS,
    deliveryProviderLabel,
    resolveDeliveryProvider,
    validateDeliveryAddress
  };
}
